import _ from 'lodash';

export type ActivityLogModelType =
  | 'CONTACT'
  | 'FUND_OPPORTUNITY'
  | 'INVESTOR'
  | 'ADMIN_USER'
  | 'INVESTMENT_ENTITY'
  | 'GLOBAL_CONFIG'
  | 'ARTICLE'
  | 'NEWS_ITEM'
  | 'CLIENT_EXCEPTION';
export const activityLogModelToContentModel: (modelType: ActivityLogModelType) => string = (
  modelType: ActivityLogModelType,
) => {
  const camelCaseModelType = _.camelCase(modelType);
  return camelCaseModelType.charAt(0).toUpperCase() + camelCaseModelType.slice(1);
};

export type ActivityLogEventType =
  | 'EOI_SUBMITTED'
  | 'EOI_UPDATED'
  | 'EOI_WITHDRAWN'
  | 'OPPORTUNITY_CREATED'
  | 'OPPORTUNITY_UPDATED'
  | 'OPPORTUNITY_PROMOTED'
  | 'CONTENT_DRAFT_SAVED'
  | 'CONTENT_PUBLISHED'
  | 'CONTENT_REVERTED'
  | 'CONTENT_AI_GENERATED'
  | 'CONTENT_AI_REFINED'
  | 'ARTICLE_AI_GENERATED'
  | 'ARTICLE_AI_REFINED'
  | 'ARTICLE_CREATED'
  | 'ARTICLE_APPROVED'
  | 'ARTICLE_PUBLISHED'
  | 'INVESTOR_CREATED'
  | 'INVESTOR_UPDATED'
  | 'INVESTOR_PROMOTED'
  | 'CONTACT_CREATED'
  | 'CONTACT_UPDATED'
  | 'CONTACT_LINK_GENERATED'
  | 'ADMIN_USER_CREATED'
  | 'ADMIN_USER_UPDATED'
  | 'ADMIN_USER_LINK_GENERATED'
  | 'GLOBAL_CONFIG_UPDATED'
  | 'USER_CREATED'
  | 'INVITATION_LINK_GENERATED'
  | 'LOGIN'
  | 'LOGOUT'
  | 'ALLOCATION_CREATED'
  | 'ALLOCATION_UPDATED'
  | 'CONTACT_ADDED_TO_ENTITY'
  | 'CONTACT_REMOVED_FROM_ENTITY'
  | 'INVESTMENT_ENTITY_CREATED'
  | 'INVESTMENT_ENTITY_UPDATED'
  | 'FUND_OPPORTUNITY_CREATED'
  | 'FUND_OPPORTUNITY_UPDATED'
  | 'FUND_OPPORTUNITY_PDF_REQUESTED'
  | 'AI_CHAT_DISCLAIMER_ACCEPTED'
  | 'ADVISOR_INVITATION_CREATED'
  | 'ADVISOR_INVITATION_VERIFIED'
  | 'ADVISOR_INVITATION_ACCEPTED'
  | 'ADVISOR_INVITATION_REJECTED'
  | 'ADVISOR_INVITATION_REGENERATED'
  | 'ADVISOR_INVITATION_CANCELLED'
  | 'ADVISOR_EOI_SUGGESTED'
  | 'ADVISOR_EOI_ACCEPTED'
  | 'ADVISOR_EOI_REJECTED'
  | 'ADVISOR_EOI_WITHDRAWN'
  | 'ADVISOR_ROLE_UPDATED'
  | 'ADVISOR_REMOVED'
  | 'ADVISOR_MODE_ENTERED'
  | 'ADVISOR_MODE_EXITED'
  | 'USER_PROFILE_UPDATED'
  | 'EMAIL_SENT'
  | 'EMAIL_SEND_FAILED'
  | 'EMAIL_OPENED'
  | 'EMAIL_CLICKED'
  | 'EMAIL_CHANGE_PENDING'
  | 'EMAIL_CHANGE_CONFIRMED'
  | 'EMAIL_CHANGE_REJECTED'
  | 'PUSH_NOTIFICATION_SENT'
  | 'PUSH_NOTIFICATION_DELIVERED'
  | 'PUSH_NOTIFICATION_OPENED'
  | 'PUSH_NOTIFICATION_DISMISSED'
  | 'NEWS_ITEM_CLICKED'
  | 'CLIENT_EXCEPTION_STATUS_CHANGED';

export interface ActivityLogLinkedModel {
  linkedModelType: ActivityLogModelType;
  linkedModelExternalId: string;
}

export interface ActivityLogLinkedModelWithDescription extends ActivityLogLinkedModel {
  description: {
    name?: string;
    contactName?: string;
    fundOpportunityName?: string;
    entityName?: string;
  };
}

export interface ActivityLogAssociatedModelDescription {
  name?: string;
  contactName?: string;
  fundOpportunityName?: string;
  entityName?: string;
}

export interface ActivityLogInitiatorDescription {
  name?: string;
  contactName?: string;
  fundOpportunityName?: string;
  entityName?: string;
  // NEW: Impersonation information directly in initiatedByDescription
  isImpersonated?: boolean;
  onBehalfOfName?: string;
  onBehalfOfType?: ActivityLogModelType;
  onBehalfOfExternalId?: string;
}

export interface ActivityLogImpersonationContext {
  impersonatedBy: string;
  impersonatedAt: string;
}

export interface ActivityLogExtendedInfo {
  impersonationContext?: ActivityLogImpersonationContext;
  [key: string]: any;
}

export interface ActivityLog {
  id: string;
  eventType: ActivityLogEventType;
  initiatedByType: ActivityLogModelType;
  initiatedByExternalId: string;

  // NEW: Impersonation fields
  onBehalfOfType?: ActivityLogModelType;
  onBehalfOfExternalId?: string;

  associatedModelType: ActivityLogModelType;
  associatedModelExternalId: string;
  associatedModelDescription?: ActivityLogAssociatedModelDescription;
  linkedModels: ActivityLogLinkedModel[];
  linkedModelsWithDescriptions?: ActivityLogLinkedModelWithDescription[];
  eventTimestamp: string;
  extendedInfo?: ActivityLogExtendedInfo;
  extendedInfoPath?: string;
  originalContentPath?: string;
  newContentPath?: string;
}

// New enhanced response structure with descriptions
export interface ActivityLogWithDescriptions {
  log: ActivityLog;
  associatedModelDescription?: ActivityLogAssociatedModelDescription;
  initiatedByDescription?: ActivityLogInitiatorDescription;
  linkedModelDescriptions: ActivityLogLinkedModelWithDescription[];
}

export interface ActivityLogResponse {
  data: ActivityLogWithDescriptions[];
  categorizedLogs: {
    actionsInitiatedBy: string[];
    actionsOnEntity: string[];
    actionsWithEntityLinked: string[];
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters?: {
    eventType?: string;
    associatedModelType?: string;
    startDate?: string;
    endDate?: string;
  };
}

export interface ActivityLogFilters {
  eventType?: ActivityLogEventType;
  associatedModelType?: ActivityLogModelType;
  dateRange?: { start: Date; end: Date };
  initiatedByType?: 'CONTACT' | 'ADMIN_USER';
  showImpersonated?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface ActivityLogEventConfig {
  icon: string;
  color: string;
  label: string;
}

export const activityLogEventConfig: Record<
  ActivityLogEventType | 'DEFAULT',
  ActivityLogEventConfig
> = {
  EOI_SUBMITTED: { icon: '📝', color: 'green', label: 'EOI Submitted' },
  EOI_UPDATED: { icon: '✏️', color: 'blue', label: 'EOI Updated' },
  EOI_WITHDRAWN: { icon: '❌', color: 'red', label: 'EOI Withdrawn' },
  OPPORTUNITY_CREATED: { icon: '➕', color: 'green', label: 'Opportunity Created' },
  OPPORTUNITY_UPDATED: { icon: '✏️', color: 'blue', label: 'Opportunity Updated' },
  OPPORTUNITY_PROMOTED: { icon: '🚀', color: 'purple', label: 'Opportunity Promoted' },
  CONTENT_DRAFT_SAVED: { icon: '💾', color: 'blue', label: 'Content Draft Saved' },
  CONTENT_PUBLISHED: { icon: '📢', color: 'green', label: 'Content Published' },
  CONTENT_REVERTED: { icon: '↩️', color: 'orange', label: 'Content Reverted' },
  CONTENT_AI_GENERATED: { icon: '🤖', color: 'purple', label: 'Content AI Generated' },
  CONTENT_AI_REFINED: { icon: '🔧', color: 'purple', label: 'Content AI Refined' },
  ARTICLE_AI_GENERATED: { icon: '🤖', color: 'purple', label: 'Article AI Generated' },
  ARTICLE_AI_REFINED: { icon: '🔧', color: 'purple', label: 'Article AI Refined' },
  ARTICLE_CREATED: { icon: '📄', color: 'green', label: 'Article Created' },
  ARTICLE_APPROVED: { icon: '✅', color: 'blue', label: 'Article Approved' },
  ARTICLE_PUBLISHED: { icon: '📢', color: 'green', label: 'Article Published' },
  INVESTOR_CREATED: { icon: '👤', color: 'green', label: 'Investor Created' },
  INVESTOR_UPDATED: { icon: '✏️', color: 'blue', label: 'Investor Updated' },
  INVESTOR_PROMOTED: { icon: '⭐', color: 'gold', label: 'Investor Promoted' },
  CONTACT_CREATED: { icon: '👤', color: 'green', label: 'Contact Created' },
  CONTACT_UPDATED: { icon: '✏️', color: 'blue', label: 'Contact Updated' },
  CONTACT_LINK_GENERATED: { icon: '🔗', color: 'orange', label: 'Contact Link Generated' },
  ADMIN_USER_CREATED: { icon: '👨‍💼', color: 'green', label: 'Admin User Created' },
  ADMIN_USER_UPDATED: { icon: '✏️', color: 'blue', label: 'Admin User Updated' },
  ADMIN_USER_LINK_GENERATED: { icon: '🔗', color: 'orange', label: 'Admin User Link Generated' },
  GLOBAL_CONFIG_UPDATED: { icon: '⚙️', color: 'gray', label: 'Global Config Updated' },
  USER_CREATED: { icon: '👤', color: 'green', label: 'User Created' },
  INVITATION_LINK_GENERATED: { icon: '🔗', color: 'orange', label: 'Invitation Link Generated' },
  LOGIN: { icon: '🔑', color: 'green', label: 'Login' },
  LOGOUT: { icon: '🚪', color: 'red', label: 'Logout' },
  ALLOCATION_CREATED: { icon: '💰', color: 'green', label: 'Allocation Created' },
  ALLOCATION_UPDATED: { icon: '💰', color: 'blue', label: 'Allocation Updated' },
  CONTACT_ADDED_TO_ENTITY: { icon: '➕', color: 'green', label: 'Contact Added to Entity' },
  CONTACT_REMOVED_FROM_ENTITY: { icon: '➖', color: 'red', label: 'Contact Removed from Entity' },
  INVESTMENT_ENTITY_CREATED: { icon: '🏢', color: 'green', label: 'Investment Entity Created' },
  INVESTMENT_ENTITY_UPDATED: { icon: '✏️', color: 'blue', label: 'Investment Entity Updated' },
  FUND_OPPORTUNITY_CREATED: { icon: '💰', color: 'green', label: 'Fund Opportunity Created' },
  FUND_OPPORTUNITY_UPDATED: { icon: '✏️', color: 'blue', label: 'Fund Opportunity Updated' },
  FUND_OPPORTUNITY_PDF_REQUESTED: {
    icon: '📄',
    color: 'purple',
    label: 'Fund Opportunity PDF Requested',
  },
  AI_CHAT_DISCLAIMER_ACCEPTED: { icon: '✅', color: 'green', label: 'AI Chat Disclaimer Accepted' },
  ADVISOR_INVITATION_CREATED: { icon: '📧', color: 'blue', label: 'Advisor Invitation Created' },
  ADVISOR_INVITATION_VERIFIED: { icon: '✅', color: 'blue', label: 'Advisor Invitation Verified' },
  ADVISOR_INVITATION_ACCEPTED: { icon: '✅', color: 'green', label: 'Advisor Invitation Accepted' },
  ADVISOR_INVITATION_REJECTED: { icon: '❌', color: 'red', label: 'Advisor Invitation Rejected' },
  ADVISOR_INVITATION_REGENERATED: {
    icon: '🔄',
    color: 'orange',
    label: 'Advisor Invitation Regenerated',
  },
  ADVISOR_INVITATION_CANCELLED: { icon: '🚫', color: 'red', label: 'Advisor Invitation Cancelled' },
  ADVISOR_EOI_SUGGESTED: { icon: '💡', color: 'blue', label: 'Advisor EOI Suggested' },
  ADVISOR_EOI_ACCEPTED: { icon: '✅', color: 'green', label: 'Advisor EOI Accepted' },
  ADVISOR_EOI_REJECTED: { icon: '❌', color: 'red', label: 'Advisor EOI Rejected' },
  ADVISOR_EOI_WITHDRAWN: { icon: '↩️', color: 'orange', label: 'Advisor EOI Withdrawn' },
  ADVISOR_ROLE_UPDATED: { icon: '👤', color: 'blue', label: 'Advisor Role Updated' },
  ADVISOR_REMOVED: { icon: '🗑️', color: 'red', label: 'Advisor Removed' },
  ADVISOR_MODE_ENTERED: { icon: '🔐', color: 'purple', label: 'Advisor Mode Entered' },
  ADVISOR_MODE_EXITED: { icon: '🚪', color: 'gray', label: 'Advisor Mode Exited' },
  USER_PROFILE_UPDATED: { icon: '✏️', color: 'blue', label: 'User Profile Updated' },
  EMAIL_SENT: { icon: '📧', color: 'green', label: 'Email Sent' },
  EMAIL_SEND_FAILED: { icon: '❌', color: 'red', label: 'Email Send Failed' },
  EMAIL_OPENED: { icon: '👁️', color: 'blue', label: 'Email Opened' },
  EMAIL_CLICKED: { icon: '🖱️', color: 'purple', label: 'Email Clicked' },
  EMAIL_CHANGE_PENDING: { icon: '⏳', color: 'orange', label: 'Email Change Pending' },
  EMAIL_CHANGE_CONFIRMED: { icon: '✅', color: 'green', label: 'Email Change Confirmed' },
  EMAIL_CHANGE_REJECTED: { icon: '❌', color: 'red', label: 'Email Change Rejected' },
  PUSH_NOTIFICATION_SENT: { icon: '📲', color: 'green', label: 'Push Notification Sent' },
  PUSH_NOTIFICATION_DELIVERED: { icon: '✓', color: 'blue', label: 'Push Notification Delivered' },
  PUSH_NOTIFICATION_OPENED: { icon: '👁️', color: 'blue', label: 'Push Notification Opened' },
  PUSH_NOTIFICATION_DISMISSED: { icon: '⊘', color: 'gray', label: 'Push Notification Dismissed' },
  NEWS_ITEM_CLICKED: { icon: '📰', color: 'blue', label: 'News Item Viewed' },
  CLIENT_EXCEPTION_STATUS_CHANGED: { icon: '🐞', color: 'blue', label: 'Client Exception Triaged' },
  DEFAULT: { icon: '🕒', color: 'gray', label: 'Unknown Event' },
};

const pushNotificationTypeLabels: Record<string, string> = {
  referral_opportunity_shared: 'Investment Opportunity Shared',
  eoi_submitted: 'EOI Submitted',
  eoi_updated: 'EOI Updated',
  eoi_suggested: 'EOI Suggested',
  promotion_immediate: 'Promotion (Immediate)',
  promotion_delayed: 'Promotion (Delayed)',
  delayed_promotion: 'Delayed Promotion',
  progress_update: 'Fund Progress Update',
  cashflow_report: 'Cashflow Report Update',
};

export function getPushNotificationEventLabel(
  eventType: ActivityLogEventType,
  extendedInfo?: ActivityLogExtendedInfo | null,
): string | null {
  const pushEvents: ActivityLogEventType[] = [
    'PUSH_NOTIFICATION_SENT',
    'PUSH_NOTIFICATION_DELIVERED',
    'PUSH_NOTIFICATION_OPENED',
    'PUSH_NOTIFICATION_DISMISSED',
  ];
  if (!pushEvents.includes(eventType) || !extendedInfo) return null;
  if (typeof extendedInfo.title === 'string' && extendedInfo.title.trim()) {
    return extendedInfo.title;
  }
  const notificationType = extendedInfo.notificationType as string | undefined;
  if (notificationType && pushNotificationTypeLabels[notificationType]) {
    return pushNotificationTypeLabels[notificationType];
  }
  return null;
}
